import { v4 as uuidv4 } from "uuid";

import { PROCESS_STATUS } from "@/store/subscriptionCore/processConstants";
import { deactivateProperty } from "@/store/propertyCore/propertyApi";
import { propertyIdToDetailsPath } from "@/utils/propertyUrlUtils";

/**
 * @param {object} stores
 * @param {string} customer
 * @param {string} propertyId
 */
export function applyPropertyDeactivationAccepted(stores, customer, propertyId) {
  stores?.propertyDetailsStore?.markPropertyDeactivating?.(customer, propertyId);
}

/**
 * @param {object} stores
 * @param {string} customer
 * @param {string} propertyId
 */
export function applyPropertyDeactivationComplete(stores, customer, propertyId) {
  stores?.propertyDetailsStore?.markPropertyDeactivated?.(customer, propertyId);
  stores?.opportunitiesStore?.markDealsInactiveForProperty?.(propertyId);
  stores?.addedPropertyReportsWatcherStore?.unregister?.(customer, [propertyId]);
}

/**
 * @param {object} stores
 * @param {string} customer
 * @param {string} propertyId
 * @returns {Promise<void>}
 */
async function reconcilePropertyAfterDeactivationFailure(stores, customer, propertyId) {
  const loadProperty = stores?.propertyDetailsStore?.loadProperty;
  if (typeof loadProperty !== "function" || !customer || !propertyId) return;
  try {
    await loadProperty({ customer, propertyId });
  } catch {
    // Server state stays authoritative when refresh fails.
  }
}

/**
 * @param {Object} params
 * @param {string} params.customer
 * @param {string} params.propertyId
 * @param {string} params.userEmail
 * @param {Object} context
 * @param {Object} stores
 * @returns {Promise<Object>}
 */
export async function runPropertyDeactivateProcess(params, context, stores) {
  const customer = String(params?.customer ?? "").trim();
  const propertyId = String(params?.propertyId ?? "").trim();
  const userEmail = String(params?.userEmail ?? "").trim();

  if (!customer || !propertyId || !userEmail) {
    throw new Error("Missing required fields for property deactivation");
  }

  const subscriptionId = uuidv4();
  let accepted = false;

  context.setProgress({ status: PROCESS_STATUS.INVOKING, message: "Starting property deactivation..." });

  try {
    const payload = await deactivateProperty({
      customer,
      propertyId,
      userEmail,
      subscriptionId,
      onAccepted: () => {
        accepted = true;
        applyPropertyDeactivationAccepted(stores, customer, propertyId);
        context.setCorrelationId(subscriptionId);
        context.setProgress({ status: PROCESS_STATUS.PROCESSING, message: "Deactivating property..." });
      },
    });

    applyPropertyDeactivationComplete(stores, customer, propertyId);

    const propertyActionPath = propertyIdToDetailsPath(propertyId);

    return {
      correlationId: subscriptionId,
      payload,
      notification: {
        outcome: "success",
        message: "Property deactivated.",
        actionRoute: propertyActionPath,
        actionLabel: propertyActionPath ? "View Property" : null,
      },
    };
  } catch (err) {
    if (err?.statusCode === 409) {
      applyPropertyDeactivationComplete(stores, customer, propertyId);
      const propertyActionPath = propertyIdToDetailsPath(propertyId);
      return {
        correlationId: subscriptionId,
        payload: { property_id: propertyId, activation_state: "DEACTIVATED" },
        notification: {
          outcome: "success",
          message: "Property is already deactivated.",
          actionRoute: propertyActionPath,
          actionLabel: propertyActionPath ? "View Property" : null,
        },
      };
    }
    if (accepted || err?.timedOutAfterAccept) {
      await reconcilePropertyAfterDeactivationFailure(stores, customer, propertyId);
    }
    throw err;
  }
}
